import React, { useEffect, useState, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError, resolveMediaUrl } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dropzone } from "@/components/Dropzone";
import { HeroCropModal } from "@/components/HeroCropModal";
import { toast } from "sonner";

const TAB_KEYS = ["films", "gallery", "testimonials", "inquiries", "site"];

export default function Admin() {
  const { user, initialised, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("films");

  if (!initialised) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1A1514]">
        <p className="text-[10px] tracking-[0.42em] uppercase text-ink-taupe">Authenticating…</p>
      </div>
    );
  }
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#1A1514] text-ink-ivory">
      <header className="px-6 md:px-12 py-6 md:py-8 border-b border-ink-ivory/10 flex items-center justify-between">
        <div>
          <p className="text-[10px] tracking-[0.42em] uppercase text-ink-taupe">Studio Atelier</p>
          <h1 className="mt-1 font-display text-2xl md:text-3xl tracking-tight">Cineglimpse · Admin</h1>
        </div>
        <div className="flex items-center gap-4">
          <button
            data-testid="admin-go-home"
            onClick={() => navigate("/")}
            className="text-[10px] tracking-[0.32em] uppercase editorial-link"
          >
            View site →
          </button>
          <button
            data-testid="admin-logout"
            onClick={() => { logout(); window.location.assign("/"); }}
            className="text-[10px] tracking-[0.32em] uppercase text-ink-champagne editorial-link"
          >
            Sign out
          </button>
        </div>
      </header>

      <main className="px-6 md:px-12 py-10">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-transparent border border-ink-ivory/10 p-1 mb-10">
            {TAB_KEYS.map((k) => (
              <TabsTrigger
                key={k}
                value={k}
                data-testid={`admin-tab-${k}`}
                className="text-[10px] tracking-[0.32em] uppercase data-[state=active]:bg-ink-ivory data-[state=active]:text-ink-espresso"
              >
                {k}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="films"><FilmsPanel /></TabsContent>
          <TabsContent value="gallery"><PhotosPanel /></TabsContent>
          <TabsContent value="testimonials"><TestimonialsPanel /></TabsContent>
          <TabsContent value="inquiries"><InquiriesPanel /></TabsContent>
          <TabsContent value="site"><SitePanel /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

/* -------- Films -------- */
function FilmsPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", couple: "", location: "", year: "", video_url: "", cover_image: "", description: "", featured: false });
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [dragIndex, setDragIndex] = useState(-1);
  const [overIndex, setOverIndex] = useState(-1);

  const load = useCallback(async () => {
    const { data } = await api.get("/films");
    setItems(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  function resetForm() {
    setForm({ title: "", couple: "", location: "", year: "", video_url: "", cover_image: "", description: "", featured: false });
    setEditingId(null);
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.title || !form.video_url) { toast.error("Title and Video URL are required."); return; }
    setBusy(true);
    try {
      if (editingId) {
        await api.put(`/admin/films/${editingId}`, form);
        toast.success("Film updated.");
      } else {
        await api.post("/admin/films", form);
        toast.success("Film added.");
      }
      resetForm();
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed."); }
    finally { setBusy(false); }
  }

  function startEdit(f) {
    setEditingId(f.id);
    setForm({
      title: f.title || "",
      couple: f.couple || "",
      location: f.location || "",
      year: f.year || "",
      video_url: f.video_url || "",
      cover_image: f.cover_image || "",
      description: f.description || "",
      featured: !!f.featured,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function remove(id) {
    if (!window.confirm("Delete this film?")) return;
    try { await api.delete(`/admin/films/${id}`); toast.success("Removed."); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed."); }
  }

  async function commitReorder(next) {
    setItems(next);
    try {
      await api.post("/admin/films/reorder", { ordered_ids: next.map((f) => f.id) });
    } catch (err) {
      toast.error("Could not save order.");
      await load();
    }
  }

  function onDragStart(i) { setDragIndex(i); }
  function onDragOver(i, e) { e.preventDefault(); setOverIndex(i); }
  function onDrop(i) {
    if (dragIndex < 0 || dragIndex === i) { setDragIndex(-1); setOverIndex(-1); return; }
    const next = [...items];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(i, 0, moved);
    setDragIndex(-1);
    setOverIndex(-1);
    commitReorder(next);
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
      <form onSubmit={submit} data-testid="admin-film-form" className="lg:col-span-5 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl">{editingId ? "Edit Film" : "Add Film"}</h2>
          {editingId && (
            <button type="button" onClick={resetForm} data-testid="admin-film-cancel-edit" className="text-[10px] tracking-[0.32em] uppercase editorial-link">
              Cancel
            </button>
          )}
        </div>
        <Input label="Title *" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="admin-film-title" />
        <Input label="Video URL (YouTube / Vimeo) *" value={form.video_url} onChange={(v) => setForm({ ...form, video_url: v })} testid="admin-film-url" />
        <div className="grid grid-cols-2 gap-4">
          <Input label="Couple" value={form.couple} onChange={(v) => setForm({ ...form, couple: v })} testid="admin-film-couple" />
          <Input label="Year" value={form.year} onChange={(v) => setForm({ ...form, year: v })} testid="admin-film-year" />
        </div>
        <Input label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testid="admin-film-location" />
        <Input label="Cover image URL (optional)" value={form.cover_image} onChange={(v) => setForm({ ...form, cover_image: v })} testid="admin-film-cover" />
        <label className="block">
          <span className="text-[10px] tracking-[0.32em] uppercase text-ink-taupe">Description</span>
          <textarea
            rows={4}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="admin-film-description"
            className="field-underline mt-2 resize-none"
          />
        </label>
        <button type="submit" disabled={busy} data-testid="admin-film-submit" className="luxe-btn disabled:opacity-50">
          {busy ? "Saving…" : (editingId ? "Save changes" : "Add film")}
        </button>
      </form>

      <div className="lg:col-span-7">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-display text-2xl">Films ({items.length})</h2>
          {items.length > 1 && (
            <p className="text-[10px] tracking-[0.32em] uppercase text-ink-taupe">Drag rows to reorder</p>
          )}
        </div>
        <ul className="border border-ink-ivory/10" data-testid="admin-films-list">
          {items.length === 0 && <li className="p-8 text-sm text-ink-ivory/50">No films yet.</li>}
          {items.map((f, i) => (
            <li
              key={f.id}
              draggable
              onDragStart={() => onDragStart(i)}
              onDragOver={(e) => onDragOver(i, e)}
              onDrop={() => onDrop(i)}
              onDragEnd={() => { setDragIndex(-1); setOverIndex(-1); }}
              data-testid={`admin-film-row-${f.id}`}
              className={`p-5 flex items-center gap-4 border-t border-ink-ivory/10 first:border-t-0 transition-colors duration-300 cursor-grab active:cursor-grabbing
                ${overIndex === i && dragIndex !== i ? "bg-ink-champagne/[0.05]" : ""}
                ${dragIndex === i ? "opacity-50" : ""}
              `}
            >
              <span className="text-ink-taupe text-xs tracking-widest select-none">⋮⋮</span>
              <div className="w-24 h-14 bg-[#1F1817] overflow-hidden border border-ink-ivory/10 shrink-0">
                {(f.thumbnail || f.cover_image) && (
                  <img src={resolveMediaUrl(f.cover_image || f.thumbnail)} alt="" className="w-full h-full object-cover" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display text-lg truncate">{f.title}</p>
                <p className="text-[10px] tracking-[0.32em] uppercase text-ink-taupe truncate">
                  {[f.couple, f.location, f.year].filter(Boolean).join(" · ")}
                </p>
              </div>
              <button
                type="button"
                onClick={() => startEdit(f)}
                data-testid={`admin-film-edit-${f.id}`}
                className="text-[10px] tracking-[0.32em] uppercase text-ink-champagne editorial-link"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => remove(f.id)}
                data-testid={`admin-film-delete-${f.id}`}
                className="text-[10px] tracking-[0.32em] uppercase text-red-300 editorial-link"
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* -------- Photos -------- */
function PhotosPanel() {
  const [items, setItems] = useState([]);
  const [queue, setQueue] = useState([]); // [{key, file, preview, progress, status, error}]
  const [defaultOrientation, setDefaultOrientation] = useState("landscape");
  const [defaultCollection, setDefaultCollection] = useState("");
  const [defaultFeatured, setDefaultFeatured] = useState(false);
  const [dragIndex, setDragIndex] = useState(-1);
  const [overIndex, setOverIndex] = useState(-1);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ caption: "", collection: "", orientation: "landscape" });
  const [framingPhoto, setFramingPhoto] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/photos");
    setItems(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Cleanup blob URLs when queue changes
  useEffect(() => () => queue.forEach((q) => q.preview && URL.revokeObjectURL(q.preview)), [queue]);

  function onFiles(files) {
    const next = files.map((f) => ({
      key: `${f.name}-${f.size}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      file: f,
      preview: URL.createObjectURL(f),
      progress: 0,
      status: "queued",
      error: "",
    }));
    setQueue((q) => [...q, ...next]);
  }

  async function uploadAll() {
    const pending = queue.filter((q) => q.status === "queued" || q.status === "error");
    if (pending.length === 0) return;
    for (const item of pending) {
      setQueue((q) => q.map((x) => (x.key === item.key ? { ...x, status: "uploading", error: "" } : x)));
      try {
        const fd = new FormData();
        fd.append("file", item.file);
        fd.append("caption", "");
        fd.append("collection", defaultCollection);
        fd.append("orientation", defaultOrientation);
        fd.append("featured", defaultFeatured ? "true" : "false");
        await api.post("/admin/photos", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (ev) => {
            if (!ev.total) return;
            const pct = Math.round((ev.loaded / ev.total) * 100);
            setQueue((q) => q.map((x) => (x.key === item.key ? { ...x, progress: pct } : x)));
          },
        });
        setQueue((q) => q.map((x) => (x.key === item.key ? { ...x, status: "done", progress: 100 } : x)));
      } catch (err) {
        setQueue((q) => q.map((x) => (x.key === item.key ? { ...x, status: "error", error: formatApiError(err.response?.data?.detail) || "Upload failed" } : x)));
      }
    }
    await load();
  }

  function clearDone() {
    setQueue((q) => q.filter((x) => x.status !== "done"));
  }

  async function remove(id) {
    if (!window.confirm("Delete this photograph?")) return;
    await api.delete(`/admin/photos/${id}`);
    await load();
  }

  function startEdit(p) {
    setEditingId(p.id);
    setEditForm({ caption: p.caption || "", collection: p.collection || "", orientation: p.orientation || "landscape" });
  }
  function cancelEdit() { setEditingId(null); }
  async function saveEdit() {
    try {
      const { data } = await api.patch(`/admin/photos/${editingId}`, editForm);
      setItems((prev) => prev.map((p) => (p.id === data.id ? data : p)));
      setEditingId(null);
      toast.success("Saved.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save.");
    }
  }

  async function toggleFeatured(p) {
    try {
      const { data } = await api.patch(`/admin/photos/${p.id}`, { featured: !p.featured });
      setItems((prev) => prev.map((x) => (x.id === data.id ? data : x)));
      toast.success(data.featured ? "Marked as hero-worthy." : "Removed from hero rotation.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update.");
    }
  }

  async function commitReorder(next) {
    setItems(next);
    try {
      await api.post("/admin/photos/reorder", { ordered_ids: next.map((p) => p.id) });
    } catch {
      toast.error("Could not save order.");
      await load();
    }
  }
  function onDragStart(i) { setDragIndex(i); }
  function onDragOver(i, e) { e.preventDefault(); setOverIndex(i); }
  function onDrop(i) {
    if (dragIndex < 0 || dragIndex === i) { setDragIndex(-1); setOverIndex(-1); return; }
    const next = [...items];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(i, 0, moved);
    setDragIndex(-1); setOverIndex(-1);
    commitReorder(next);
  }

  const uploadingCount = queue.filter((q) => q.status === "uploading").length;
  const queuedCount = queue.filter((q) => q.status === "queued").length;
  const doneCount = queue.filter((q) => q.status === "done").length;

  return (
    <div className="space-y-12">
      {/* Upload zone */}
      <section data-testid="admin-photo-upload-section">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-display text-2xl">Upload Photographs</h2>
          <p className="text-[10px] tracking-[0.32em] uppercase text-ink-taupe">Drag-and-drop · multiple files</p>
        </div>
        <Dropzone testid="admin-photo-dropzone" onFiles={onFiles} disabled={uploadingCount > 0} />

        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <label className="block">
            <span className="text-[10px] tracking-[0.32em] uppercase text-ink-taupe">Default orientation</span>
            <select
              data-testid="admin-photo-default-orientation"
              value={defaultOrientation}
              onChange={(e) => setDefaultOrientation(e.target.value)}
              className="field-underline mt-2"
            >
              <option value="landscape">Landscape</option>
              <option value="portrait">Portrait</option>
              <option value="square">Square</option>
            </select>
          </label>
          <Input label="Default collection (optional)" value={defaultCollection} onChange={setDefaultCollection} testid="admin-photo-default-collection" />
          <label className="flex items-center gap-3 cursor-pointer select-none pb-3">
            <input
              type="checkbox"
              checked={defaultFeatured}
              onChange={(e) => setDefaultFeatured(e.target.checked)}
              data-testid="admin-photo-default-featured"
              className="h-3.5 w-3.5 accent-ink-champagne bg-transparent"
            />
            <span className="text-[10px] tracking-[0.32em] uppercase text-ink-taupe">
              Feature on homepage hero
            </span>
          </label>
          <div className="flex items-center gap-3 justify-end">
            {doneCount > 0 && (
              <button type="button" onClick={clearDone} data-testid="admin-photo-clear-done" className="text-[10px] tracking-[0.32em] uppercase editorial-link">
                Clear completed
              </button>
            )}
            <button
              type="button"
              onClick={uploadAll}
              disabled={queuedCount === 0 && queue.filter((q) => q.status === "error").length === 0}
              data-testid="admin-photo-upload-all"
              className="luxe-btn disabled:opacity-40"
            >
              {uploadingCount > 0 ? `Uploading… (${doneCount}/${queue.length})` : `Upload ${queuedCount || ""}`.trim()}
            </button>
          </div>
        </div>

        {queue.length > 0 && (
          <ul data-testid="admin-photo-queue" className="mt-8 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {queue.map((q) => (
              <li key={q.key} data-testid={`admin-photo-queue-item-${q.key}`} className="relative aspect-square overflow-hidden border border-ink-ivory/10 bg-[#1F1817]">
                <img src={q.preview} alt="" className="w-full h-full object-cover opacity-90" />
                <div className="absolute inset-x-0 bottom-0 px-2 py-1.5 bg-ink-espresso/85">
                  <p className="text-[9px] tracking-[0.24em] uppercase text-ink-ivory/80 truncate">
                    {q.status === "done" ? "✓ Done" : q.status === "uploading" ? `${q.progress}%` : q.status === "error" ? q.error : "Queued"}
                  </p>
                  {q.status === "uploading" && (
                    <div className="mt-1 h-px bg-ink-ivory/20">
                      <div className="h-full bg-ink-champagne transition-all duration-200" style={{ width: `${q.progress}%` }} />
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Gallery list with reorder + inline edit */}
      <section>
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-display text-2xl">Photographs ({items.length})</h2>
          {items.length > 1 && (
            <p className="text-[10px] tracking-[0.32em] uppercase text-ink-taupe">Drag to reorder · Click to edit</p>
          )}
        </div>
        {items.length === 0 ? (
          <p className="text-sm text-ink-ivory/50 p-6 border border-ink-ivory/10">No photographs yet.</p>
        ) : (
          <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="admin-photo-grid">
            {items.map((p, i) => (
              <li
                key={p.id}
                draggable={editingId !== p.id}
                onDragStart={() => onDragStart(i)}
                onDragOver={(e) => onDragOver(i, e)}
                onDrop={() => onDrop(i)}
                onDragEnd={() => { setDragIndex(-1); setOverIndex(-1); }}
                data-testid={`admin-photo-tile-${p.id}`}
                className={`relative border border-ink-ivory/10 bg-[#1F1817] transition-all duration-300
                  ${overIndex === i && dragIndex !== i ? "ring-1 ring-ink-champagne" : ""}
                  ${dragIndex === i ? "opacity-40" : ""}
                  ${editingId === p.id ? "" : "cursor-grab active:cursor-grabbing"}
                `}
              >
                <div className="aspect-square overflow-hidden relative">
                  <img src={resolveMediaUrl(p.image_url)} alt={p.caption || ""} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggleFeatured(p); }}
                    data-testid={`admin-photo-featured-${p.id}`}
                    aria-label={p.featured ? "Remove from hero rotation" : "Feature on homepage hero"}
                    title={p.featured ? "Featured on homepage hero" : "Click to feature on homepage hero"}
                    className={`absolute top-2 left-2 h-7 w-7 flex items-center justify-center rounded-full border transition-all duration-500
                      ${p.featured
                        ? "bg-ink-champagne text-ink-espresso border-ink-champagne"
                        : "bg-ink-espresso/70 text-ink-ivory/70 border-ink-ivory/20 hover:text-ink-ivory hover:border-ink-ivory/50"}`}
                  >
                    <svg viewBox="0 0 24 24" width="14" height="14" fill={p.featured ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.6">
                      <path strokeLinejoin="round" d="M12 3.6l2.6 5.4 5.9.8-4.3 4.1 1.1 5.9L12 17.1l-5.3 2.7 1.1-5.9L3.5 9.8l5.9-.8L12 3.6z" />
                    </svg>
                  </button>
                </div>
                {editingId === p.id ? (
                  <div className="p-3 space-y-3" data-testid={`admin-photo-edit-${p.id}`}>
                    <Input label="Caption" value={editForm.caption} onChange={(v) => setEditForm({ ...editForm, caption: v })} testid={`admin-photo-edit-caption-${p.id}`} />
                    <Input label="Collection" value={editForm.collection} onChange={(v) => setEditForm({ ...editForm, collection: v })} testid={`admin-photo-edit-collection-${p.id}`} />
                    <label className="block">
                      <span className="text-[10px] tracking-[0.32em] uppercase text-ink-taupe">Orientation</span>
                      <select
                        value={editForm.orientation}
                        onChange={(e) => setEditForm({ ...editForm, orientation: e.target.value })}
                        data-testid={`admin-photo-edit-orientation-${p.id}`}
                        className="field-underline mt-2"
                      >
                        <option value="landscape">Landscape</option>
                        <option value="portrait">Portrait</option>
                        <option value="square">Square</option>
                      </select>
                    </label>
                    <div className="flex items-center justify-between pt-1">
                      <button type="button" onClick={cancelEdit} className="text-[10px] tracking-[0.32em] uppercase editorial-link" data-testid={`admin-photo-edit-cancel-${p.id}`}>
                        Cancel
                      </button>
                      <button type="button" onClick={saveEdit} className="text-[10px] tracking-[0.32em] uppercase text-ink-champagne editorial-link" data-testid={`admin-photo-edit-save-${p.id}`}>
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-[10px] tracking-[0.32em] uppercase text-ink-taupe truncate">{p.caption || "Untitled"}</p>
                      {p.collection && <p className="text-[10px] tracking-[0.32em] uppercase text-ink-taupe/70 truncate">{p.collection}</p>}
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <button type="button" onClick={() => setFramingPhoto(p)} data-testid={`admin-photo-frame-${p.id}`} className="text-[10px] tracking-[0.32em] uppercase text-ink-ivory/80 editorial-link">
                        Frame
                      </button>
                      <button type="button" onClick={() => startEdit(p)} data-testid={`admin-photo-edit-open-${p.id}`} className="text-[10px] tracking-[0.32em] uppercase text-ink-champagne editorial-link">
                        Edit
                      </button>
                      <button type="button" onClick={() => remove(p.id)} data-testid={`admin-photo-delete-${p.id}`} className="text-[10px] tracking-[0.32em] uppercase text-red-300 editorial-link">
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {framingPhoto && (
        <HeroCropModal
          photo={framingPhoto}
          onClose={() => setFramingPhoto(null)}
          onSaved={(updated) => setItems((prev) => prev.map((p) => (p.id === updated.id ? updated : p)))}
        />
      )}
    </div>
  );
}

/* -------- Testimonials -------- */
function TestimonialsPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ quote: "", author: "", role: "", location: "", featured: false });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/testimonials");
    setItems(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function submit(e) {
    e.preventDefault();
    if (!form.quote || !form.author) { toast.error("Quote and author are required."); return; }
    setBusy(true);
    try {
      await api.post("/admin/testimonials", form);
      toast.success("Testimonial added.");
      setForm({ quote: "", author: "", role: "", location: "", featured: false });
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed."); }
    finally { setBusy(false); }
  }
  async function remove(id) {
    if (!window.confirm("Delete this testimonial?")) return;
    await api.delete(`/admin/testimonials/${id}`);
    await load();
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
      <form onSubmit={submit} data-testid="admin-testimonial-form" className="lg:col-span-5 space-y-5">
        <h2 className="font-display text-2xl">Add Testimonial</h2>
        <label className="block">
          <span className="text-[10px] tracking-[0.32em] uppercase text-ink-taupe">Quote *</span>
          <textarea
            rows={5}
            value={form.quote}
            onChange={(e) => setForm({ ...form, quote: e.target.value })}
            data-testid="admin-testimonial-quote"
            className="field-underline mt-2 resize-none"
          />
        </label>
        <Input label="Author *" value={form.author} onChange={(v) => setForm({ ...form, author: v })} testid="admin-testimonial-author" />
        <div className="grid grid-cols-2 gap-4">
          <Input label="Role" value={form.role} onChange={(v) => setForm({ ...form, role: v })} testid="admin-testimonial-role" />
          <Input label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testid="admin-testimonial-location" />
        </div>
        <button type="submit" disabled={busy} data-testid="admin-testimonial-submit" className="luxe-btn disabled:opacity-50">
          {busy ? "Saving…" : "Add testimonial"}
        </button>
      </form>

      <div className="lg:col-span-7">
        <h2 className="font-display text-2xl mb-6">Testimonials ({items.length})</h2>
        <ul className="divide-y divide-ink-ivory/10 border border-ink-ivory/10">
          {items.length === 0 && <li className="p-8 text-sm text-ink-ivory/50">No testimonials yet.</li>}
          {items.map((t) => (
            <li key={t.id} data-testid={`admin-testimonial-row-${t.id}`} className="p-5">
              <p className="font-display italic text-lg text-ink-ivory">&ldquo;{t.quote}&rdquo;</p>
              <div className="mt-2 flex items-center justify-between gap-4">
                <p className="text-[10px] tracking-[0.32em] uppercase text-ink-taupe">
                  — {t.author}{(t.role || t.location) ? ` · ${[t.role, t.location].filter(Boolean).join(", ")}` : ""}
                </p>
                <button
                  onClick={() => remove(t.id)}
                  data-testid={`admin-testimonial-delete-${t.id}`}
                  className="text-[10px] tracking-[0.32em] uppercase text-red-300 editorial-link"
                >Delete</button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* -------- Inquiries -------- */
function InquiriesPanel() {
  const [items, setItems] = useState([]);
  const load = useCallback(async () => {
    const { data } = await api.get("/admin/inquiries");
    setItems(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function remove(id) {
    if (!window.confirm("Delete this inquiry?")) return;
    await api.delete(`/admin/inquiries/${id}`);
    await load();
  }

  return (
    <div>
      <h2 className="font-display text-2xl mb-6">Inquiries ({items.length})</h2>
      <ul className="space-y-4">
        {items.length === 0 && <li className="text-sm text-ink-ivory/50 p-6 border border-ink-ivory/10">No inquiries yet.</li>}
        {items.map((q) => (
          <li key={q.id} data-testid={`admin-inquiry-${q.id}`} className="border border-ink-ivory/10 p-5">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="font-display text-xl">{q.name}</p>
                <p className="text-[10px] tracking-[0.32em] uppercase text-ink-taupe">
                  <a href={`mailto:${q.email}`} className="editorial-link">{q.email}</a>
                  {q.phone && (
                    <>
                      <span className="mx-2 text-ink-ivory/30">·</span>
                      <a href={`tel:${q.phone}`} className="editorial-link" data-testid={`admin-inquiry-phone-${q.id}`}>{q.phone}</a>
                    </>
                  )}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] tracking-[0.32em] uppercase text-ink-taupe">
                  {q.event_date || "—"}{q.event_location ? ` · ${q.event_location}` : ""}
                </p>
                <p className="text-[10px] tracking-[0.32em] uppercase text-ink-champagne">
                  {q.email_sent ? "Email sent" : "Saved"}
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm text-ink-ivory/80 whitespace-pre-wrap">{q.message}</p>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => remove(q.id)}
                data-testid={`admin-inquiry-delete-${q.id}`}
                className="text-[10px] tracking-[0.32em] uppercase text-red-300 editorial-link"
              >Delete</button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Input({ label, value, onChange, testid, type = "text" }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.32em] uppercase text-ink-taupe">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="field-underline mt-2"
      />
    </label>
  );
}

/* -------- Site settings (Manifesto showreel video) -------- */
function SitePanel() {
  const [settings, setSettings] = useState(null);
  const [urlDraft, setUrlDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const load = useCallback(async () => {
    const { data } = await api.get("/settings");
    setSettings(data);
    setUrlDraft(data.manifesto_showreel_url || "");
  }, []);

  useEffect(() => { load(); }, [load]);

  async function saveUrl() {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/settings", { manifesto_showreel_url: urlDraft.trim() });
      setSettings(data);
      setUrlDraft(data.manifesto_showreel_url || "");
      toast.success("Manifesto showreel updated.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  async function uploadVideo(file) {
    if (!file) return;
    if (!/^video\//.test(file.type) && !/\.(mp4|webm|mov|m4v)$/i.test(file.name || "")) {
      toast.error("Please choose an MP4, WebM, MOV, or M4V file.");
      return;
    }
    if (file.size > 200 * 1024 * 1024) {
      toast.error("Video exceeds 200 MB. Please compress it first.");
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/settings/showreel-upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      setSettings(data);
      setUrlDraft(data.manifesto_showreel_url || "");
      toast.success("Showreel video uploaded.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }

  async function clearShowreel() {
    if (!window.confirm("Remove the Manifesto showreel and fall back to the photo showreel?")) return;
    try {
      const { data } = await api.delete("/admin/settings/showreel");
      setSettings(data);
      setUrlDraft("");
      toast.success("Showreel cleared.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  const kind = settings?.manifesto_showreel_kind || "";
  const currentUrl = settings?.manifesto_showreel_url || "";
  const isDirectVideo = kind === "file" || (currentUrl && /\.(mp4|webm|mov|m4v)(\?.*)?$/i.test(currentUrl));
  const isEmbedOnly = kind === "youtube" || kind === "vimeo";
  const previewSrc = kind === "file" ? resolveMediaUrl(currentUrl) : currentUrl;
  const draftIsEmbed = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be|vimeo\.com)\//i.test(urlDraft.trim());
  const draftIsDirect = /\.(mp4|webm|mov|m4v)(\?.*)?$/i.test(urlDraft.trim());

  return (
    <div className="max-w-4xl">
      <div className="mb-10">
        <p className="text-[10px] tracking-[0.42em] uppercase text-ink-taupe">Homepage</p>
        <h2 className="mt-2 font-display text-3xl md:text-4xl tracking-tight text-ink-ivory">
          Manifesto showreel
        </h2>
        <p className="mt-4 max-w-2xl text-sm text-ink-ivory/70 leading-relaxed">
          Replace the Manifesto's photograph with a cinematic video. The homepage
          only uses direct <span className="text-ink-ivory">.mp4 / .webm / .mov / .m4v</span> files as
          backgrounds — <em>YouTube and Vimeo cannot be used</em> because their embedded
          players always expose player chrome (play buttons, branding, controls),
          which breaks the editorial aesthetic. Upload your MP4 below.
        </p>
      </div>

      {/* PRIMARY — file upload */}
      <section className="border border-ink-champagne/25 p-6 md:p-8 mb-8 bg-[#1A1514]/30">
        <div className="flex items-center justify-between gap-4 mb-4">
          <p className="text-[10px] tracking-[0.32em] uppercase text-ink-champagne">
            Recommended · Upload an MP4
          </p>
          <span className="text-[9px] tracking-[0.32em] uppercase text-ink-taupe">Primary workflow</span>
        </div>
        <p className="text-sm text-ink-ivory/70 leading-relaxed mb-6 max-w-2xl">
          Muted, looping, autoplay, no controls — behaves exactly like the photo
          background, with elegant cinematic motion. Landscape 15–30 s clips
          read best. Max 200 MB.
        </p>
        <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
          <label
            className={`luxe-btn cursor-pointer ${uploading ? "opacity-50 cursor-wait" : ""}`}
            data-testid="admin-showreel-upload-label"
          >
            {uploading ? `Uploading… ${progress}%` : "Choose video file"}
            <input
              type="file"
              accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov,.m4v"
              className="hidden"
              disabled={uploading}
              data-testid="admin-showreel-upload-input"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) uploadVideo(f);
              }}
            />
          </label>
          <p className="text-[11px] text-ink-ivory/50 leading-relaxed">
            MP4 · WebM · MOV · M4V
          </p>
        </div>
      </section>

      {/* SECONDARY — direct MP4 URL (for those who host elsewhere) */}
      <section className="border border-ink-ivory/10 p-6 md:p-8 mb-8">
        <div className="flex items-center justify-between gap-4 mb-4">
          <p className="text-[10px] tracking-[0.32em] uppercase text-ink-champagne">
            Advanced · Direct .mp4 URL
          </p>
          <span className="text-[9px] tracking-[0.32em] uppercase text-ink-taupe">Optional fallback</span>
        </div>
        <p className="text-sm text-ink-ivory/60 leading-relaxed mb-4 max-w-2xl">
          Paste a URL that ends in <span className="text-ink-ivory">.mp4</span> /
          <span className="text-ink-ivory"> .webm</span> /
          <span className="text-ink-ivory"> .mov</span> — e.g. a Cloudflare Stream, Bunny.net,
          Vimeo Pro progressive URL, or a file you host yourself. Only direct
          video files play as backgrounds; YouTube / Vimeo watch-page links
          will be rejected as background sources.
        </p>
        <div className="flex flex-col md:flex-row gap-4">
          <input
            data-testid="admin-showreel-url"
            type="url"
            value={urlDraft}
            onChange={(e) => setUrlDraft(e.target.value)}
            placeholder="https://cdn.example.com/showreel.mp4"
            className="field-underline flex-1"
          />
          <button
            type="button"
            onClick={saveUrl}
            disabled={saving || uploading}
            data-testid="admin-showreel-save"
            className="luxe-btn disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save URL"}
          </button>
        </div>
        {urlDraft.trim() && draftIsEmbed && (
          <p
            data-testid="admin-showreel-embed-warning"
            className="mt-4 text-[11px] tracking-[0.14em] uppercase text-red-300 leading-relaxed"
          >
            ⚠ YouTube / Vimeo watch-page links cannot be used as a background — their
            embedded player always shows a play button and branding. The homepage
            will keep the photo showreel and ignore this link. Please upload an MP4
            instead, or paste a direct .mp4 URL.
          </p>
        )}
        {urlDraft.trim() && !draftIsEmbed && !draftIsDirect && (
          <p className="mt-4 text-[11px] tracking-[0.14em] uppercase text-amber-200/80 leading-relaxed">
            This URL doesn't look like a direct video file. Only URLs ending in
            .mp4 / .webm / .mov / .m4v will play as a background.
          </p>
        )}
      </section>

      {/* Current showreel — clearly indicates whether it's actually used */}
      <section className="border border-ink-ivory/10 p-6 md:p-8">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] tracking-[0.32em] uppercase text-ink-champagne">Currently saved</p>
            <p
              className="mt-2 text-sm text-ink-ivory/80 break-all"
              data-testid="admin-showreel-current-url"
            >
              {currentUrl || "— (photo showreel is showing)"}
            </p>
            {kind && (
              <p className="mt-1 text-[10px] tracking-[0.32em] uppercase text-ink-taupe">
                Source · {kind === "file" ? "Uploaded MP4" : kind}
              </p>
            )}
            {isEmbedOnly && (
              <p
                data-testid="admin-showreel-embed-inactive"
                className="mt-3 text-[11px] tracking-[0.14em] uppercase text-amber-200/85 leading-relaxed max-w-xl"
              >
                ⚠ Not used as background · This is a YouTube / Vimeo link, so the
                homepage falls back to the photo showreel. Upload an MP4 to enable
                the cinematic motion background.
              </p>
            )}
            {isDirectVideo && (
              <p className="mt-3 text-[11px] tracking-[0.14em] uppercase text-emerald-300/80">
                ✓ Playing as background
              </p>
            )}
          </div>
          {currentUrl && (
            <button
              onClick={clearShowreel}
              data-testid="admin-showreel-clear"
              className="text-[10px] tracking-[0.32em] uppercase text-red-300 editorial-link"
            >
              Remove showreel
            </button>
          )}
        </div>

        {currentUrl && (
          <div className="mt-6 bg-black aspect-video max-w-2xl">
            {isDirectVideo ? (
              <video
                key={previewSrc}
                src={previewSrc}
                controls
                muted
                playsInline
                className="w-full h-full object-cover"
                data-testid="admin-showreel-preview-video"
              />
            ) : (
              <a
                href={currentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full h-full flex items-center justify-center text-[10px] tracking-[0.32em] uppercase text-ink-ivory/70 editorial-link"
                data-testid="admin-showreel-preview-link"
              >
                Open {kind || "source"} in a new tab →
              </a>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
